import styles from './index.css';

import * as React from 'react';

import {Block, FormField, Section, Note, ConditionalNote} from '@forms/LayoutComponents';
import {DateRangeSelect, DurationInput, InstitutionSelect, TripSelect, CCMReCaptcha} from '@forms/Composites';
import {AlternativeDateRangeSelect, AlternativeDurationInput} from '@forms/Composites';
import {Form, Button, Icon} from '@forms/Components';
import {GroupToursRFQFormProps} from './definitions';
import initConfig from './config';

interface Options {
    value: string;
}
interface Configuration {
    name: string;
    type: string;
    options: Options[]
    label: string;
}

const GroupToursRFQForm = ({submit, withLabel, withTrips, withInstitutions, defaultValues, $disclaimer, captchaIsActive, captchaSitekey}: GroupToursRFQFormProps) => {
	const {data: label} = withLabel();
	const hasLabel = label && Object.keys(label).length;
	const {getConfig, getLabel} = initConfig(label);

	const [force, updateState] = React.useState(true);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const [hasAlternativeDates, setHasAlternativeDates] = React.useState(false);
    const [selectedTripIds, setSelectedTripIds] = React.useState<number[]>([]);
    const [countSelectedTripIds, setCountSelectedTripIds] = React.useState<number>(0);
    const [tripData, setTripData] = React.useState<any>(null);

    const [hasAccomodation, setHasAccomodation] = React.useState(false);
    const [hasTransportation, setHasTransportation] = React.useState(false);

    const [accomodationList, setAccomodationList] = React.useState<string[]>([]);
    const [transportationList, setTransportationList] = React.useState<string[]>([]);
    const [captchaAccpeted, setCaptchaAccepted] = React.useState<boolean>(!captchaIsActive);

	React.useEffect(() => {
		hasLabel && force && updateState(false)
	});

	const onSubmit = (formData: any) => {
		if (!isSubmitting && captchaAccpeted) {
			submit(formData);
			setIsSubmitting(true);
		}
	};

	const handleTimeFlexibility = (value: string|number) => {
		if (value === getLabel('quoteRequest.timeFlexibility.1M')) {
			setHasAlternativeDates(true);
		} else {
			setHasAlternativeDates(false);
		}
	};

    const onChangeTripSelect = (tripId: number | null, data: any) => {
        setHasAccomodation(false);
        setHasTransportation(false);
        setTripData(data);
        updateSelectedTripIds(tripId);
    };

    function updateSelectedTripIds(tripId: number | null) {
        let tmp = selectedTripIds;

        if (tripId === null) {
            tmp = [];
        } else {
            if (tmp.includes(tripId)) {
                const index = tmp.indexOf(tripId);
                tmp.splice(index, 1);
            } else {
                tmp.push(tripId)
            }
        }

        setSelectedTripIds(tmp)
        setCountSelectedTripIds(tmp.length);
    }

    function getAccomodationForName (name: string) {
        let response = '';
        switch (name) {
            case 'hotel':
                response = 'Hotel, Hostel/Jugendherberge';
                break;
            case 'camping':
                response = 'Campinganlage (Mobilhomes, Zelte, etc.)';
                break;
            case 'hostFamily':
                response = 'Gastfamilie';
                break;
            case 'churchGuestHouse':
                response = 'Kirchliches Gästehaus';
                break;
            case 'bungalow':
                response = 'Ferienhaus/Bungalow';
                break;
            case 'apartment':
                response = 'Apartments/Studentenwohnheim';
                break;
            case 'other':
                response = 'Sonstiges';
                break;
        }

        return response;
    }

    function getTransportationForName (name: string) {
        let response = '';
        switch (name) {
            case 'bahnreise':
                response = 'Bahnreise';
                break;
            case 'busreise':
                response = 'Busreise';
                break;
            case 'eigenanreise':
                response = 'Eigenanreise';
                break;
            case 'flugreise':
                response = 'Flugreise';
                break;
            case 'fernbus':
                response = 'Fernbus';
                break;
            case 'aufenthaltspaket':
                response = 'Aufenthaltspaket';
                break;
        }

        return response;
    }

    function onlyUniqueArray(value: string, index: number, array: string[]) {
        return array.indexOf(value) === index;
    }

    React.useEffect(() => {
        const accResult: string[] = [];
        const transportResult: string[] = [];
        if (tripData) {
            Object.values(tripData).forEach((options) => {
                 // @ts-ignore
                 options?.forEach((option) => {
                     if (Array.isArray(selectedTripIds) && selectedTripIds.includes(Number(option.id))) {
                         if (option.accommodationTypes) {
                             Object.values(option.accommodationTypes).forEach((acc: string) => {
                                 accResult.push(acc);
                             });
                         }
                         if (option.meansOfTransports) {
                             Object.values(option.meansOfTransports).forEach((transport: any) => {
                                 // @ts-ignore
                                 transportResult.push(transport.value);

                                // Sonderhandling #96423 ... bei Aufenthaltspaket automatsch Eigenanreise anzeigen
                                if (transport.value === 'aufenthaltspaket') {
                                    transportResult.push('eigenanreise');
                                }
                            });
                         }
                     }
                 });
            });
        }

        const tmpAccomodation: string[] = [];
        const tmpTransportation: string[] = [];

        accResult.filter(onlyUniqueArray)?.forEach(accomodation => {
            if (accomodation) {
                tmpAccomodation.push(getAccomodationForName(accomodation))
            }
        });

        setAccomodationList(tmpAccomodation);
        if (tmpAccomodation.length > 0 ) {
            setHasAccomodation(true);
        }

        transportResult.filter(onlyUniqueArray)?.forEach(transport => {
            if (transport) {
                tmpTransportation.push(getTransportationForName(transport))
            }
        });

        setTransportationList(tmpTransportation);
        if (tmpTransportation.length > 0 ) {
            setHasTransportation(true);
        }
    }, [countSelectedTripIds]);

    const handleCaptcha = (value: string) => {
        if (value) {
            setCaptchaAccepted(true);
        }
    }

	return (
		hasLabel
			? (
				<Form
					onSubmit={onSubmit}
					defaultValues={defaultValues}
					className={styles.form}
				>
					<Section
						title={getLabel('title')}
						subtitle={getLabel('hint.limitTrips')}
					>
						<TripSelect
                            {...{withTrips, getLabel, onChangeTripSelect}}
                        />
					</Section>

                    {
                        (countSelectedTripIds > 0 && hasAccomodation) &&
                            <Section title={getLabel('quoteRequest.offerIncludesAccommodation')}>
                                <FormField {...{...getConfig('quoteRequest.offerIncludesAccommodation'), visibleElements: accomodationList}}/>
                            </Section>
                    }

					<Section title={getLabel('quoteRequest.itinerary')}>
						<DateRangeSelect {...{getLabel}}/>
						<Block>
							<Block half>
								<DurationInput {...{getLabel}}/>
							</Block>
							<Block half>
								<FormField
									{...getConfig('quoteRequest.timeFlexibility')}
									onChange={handleTimeFlexibility}
								/>
							</Block>
						</Block>
					</Section>

					{hasAlternativeDates &&
						<Section title={getLabel('quoteRequest.alternative')}>
							<AlternativeDateRangeSelect {...{getLabel}}/>
							<Block>
								<Block half>
									<AlternativeDurationInput {...{getLabel}}/>
								</Block>
								<Block half>
									<FormField {...getConfig('quoteRequest.alternativeTimeFlexibility')}/>
								</Block>
							</Block>
						</Section>
					}

                    {
                        (countSelectedTripIds > 0 && hasTransportation) &&
                            <Section
                                title={getLabel('quoteRequest.offerIncludesTransportation')}
                            >
                                <FormField {...{...getConfig('quoteRequest.offerIncludesTransportation'), visibleElements: transportationList}}/>
                                <FormField {...getConfig('quoteRequest.departureStation')}/>
                                <FormField {...getConfig('quoteRequest.departureAirport')}/>
                                <FormField {...getConfig('quoteRequest.departureBusStation')}/>
                            </Section>
                    }

					<Section title={getLabel('quoteRequest.offerPrice.title')}>
						<FormField {...getConfig('quoteRequest.offerPrice')}/>
					</Section>

					<Section title={getLabel('description.title')}>
						<FormField {...getConfig('description')}/>
					</Section>

					<Section title={getLabel('quoteRequest.offerIncludesParticipants')}>
						<Block>
							<Block half>
								<FormField {...getConfig('quoteRequest.offerIncludesParticipants.totalAmountOfParticipants')}/>

							</Block>
							<Block half>
								<FormField {...getConfig('quoteRequest.offerIncludesSupervisors')}/>
							</Block>
						</Block>
						<Block>
							<Note text={getLabel('quoteRequest.offerIncludesParticipants.description')} isCentered/>
						</Block>
						<Block>
							<Block half>
								<FormField {...getConfig('quoteRequest.minCustomerAge')}/>
							</Block>
							<Block half>
								<FormField {...getConfig('quoteRequest.maxCustomerAge')}/>
							</Block>

							<Block half>
								<FormField {...getConfig('quoteRequest.offerIncludesComplimentarySeats')}/>
							</Block>
							<Block half>
								<FormField {...getConfig('hidden.includesHandicap')}/>
							</Block>
							<Block>
								<FormField {...getConfig('quoteRequest.customerHandicap')}/>
							</Block>
						</Block>
					</Section>

					<Section title={getLabel('customer.title')}>
						<Block half title={getLabel('customer')}>
							<FormField {...getConfig('customer.salutation')}/>
							<FormField {...getConfig('customer.additionalType')}/>
							<ConditionalNote {...getConfig('customer.additionalTypeHint')} />
							<FormField {...getConfig('customer.givenName')}/>
							<FormField {...getConfig('customer.familyName')}/>
							<FormField {...getConfig('customer.address.streetAddress')}/>
							<Block half>
								<FormField {...getConfig('customer.address.postalCode')}/>
							</Block>
							<Block half>
								<FormField {...getConfig('customer.address.addressLocality')}/>
							</Block>
							<FormField {...getConfig('customer.email')}/>
							<FormField {...getConfig('customer.telephone')}/>
						</Block>
						<Block half title={getLabel('participant')}>
							<InstitutionSelect {...{withInstitutions, getConfig, getLabel}}/>
						</Block>
					</Section>

					<Section title={getLabel('other.title')}>
						<Block half>
							<FormField {...getConfig('referer')}/>
						</Block>
						<Block half>
							<FormField {...getConfig('newsletter')}/>
						</Block>
                        {captchaIsActive &&
                            <Block>
                                <CCMReCaptcha
                                    onChange={handleCaptcha}
                                    siteKey={captchaSitekey ?? ""}
                                />
                            </Block>
                        }
                    </Section>

					<Section isCentered>
						<div className={styles.disclaimer} dangerouslySetInnerHTML={{__html: $disclaimer.outerHTML}} />
					</Section>

					<Section isCentered>
						<Button
							type="submit"
							isText
							isDisabled={isSubmitting || !captchaAccpeted}
						>
							<span>{getLabel('submit')}</span>
							{
								isSubmitting
									? <Icon rotates name="spinner"/>
									: <Icon name="angle-double-right"/>

							}
						</Button>
					</Section>
                    {!captchaAccpeted &&
                        <Section isCentered>
                            <span className={styles.error}>
                                {getLabel('captcha.accept')}
                            </span>
                        </Section>
                    }
				</Form>
			)
			: null
	);
};

export default GroupToursRFQForm;
